// Fee System Types and Interfaces

// Entity types that can have fees assigned
export type FeeEntityType = 'site' | 'load_group' | 'charger';

// Time window days
export type DayType = 'weekday' | 'weekend' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

// Fee component types
export type FeeComponentType = 'session_fee' | 'energy_fee' | 'time_fee' | 'idle_fee' | 'reservation_fee';

// Time window for rate application
export interface TimeWindow {
  days: DayType[];
  start: string; // HH:MM format
  end: string;   // HH:MM format
}

// Energy rate configuration
export interface EnergyRate {
  name: string;
  rate: number;
  unit: 'kWh';
  timeWindows?: TimeWindow[];
}

// Session fee component
export interface SessionFeeComponent {
  enabled: boolean;
  amount: number;
  description?: string;
}

// Energy fee component
export interface EnergyFeeComponent {
  enabled: boolean;
  rates: EnergyRate[];
}

// Time rate configuration (windowed, per-unit; mirrors EnergyRate)
export interface TimeRate {
  rate: number; // PER UNIT (per minute or per hour)
  name?: string;
  timeWindows?: TimeWindow[];
}

// Time-based fee component (windowed + unit-ceiling, REF-A §A.1)
export interface TimeFeeComponent {
  enabled: boolean;
  unit: 'minute' | 'hour';
  rates: TimeRate[];
  freeMinutes?: number;
  maxChargeMinutes?: number; // 0/absent = no limit; > 0 = billing cap + auto-stop
  noLimitPreAuthAmount?: number; // used only when maxChargeMinutes == 0; 0 = no hold
}

// Idle fee component
export interface IdleFeeComponent {
  enabled: boolean;
  graceMinutes: number;
  rate: number;
  unit: 'minute';
  maxPerSession?: number;
}

// Reservation fee component
export interface ReservationFeeComponent {
  enabled: boolean;
  amount: number;
  policyId?: string | null;
}

// Special date pricing
export interface SpecialDatePricing {
  date: string; // YYYY-MM-DD
  name: string;
  rateMultiplier?: number;
  components?: FeeComponentType[];
  absoluteRates?: Partial<Record<FeeComponentType, number>>;
}

// Discount configuration (for future implementation)
export interface DiscountConfig {
  type: 'membership' | 'promotion' | 'volume' | 'loyalty';
  value: number;
  unit: 'percentage' | 'absolute';
  conditions?: Record<string, any>;
  validFrom?: string;
  validUntil?: string;
}

// Complete fee structure
export interface FeeStructure {
  version: string;
  currency: string;
  components: {
    sessionFee?: SessionFeeComponent;
    energyFee?: EnergyFeeComponent;
    timeFee?: TimeFeeComponent;
    idleFee?: IdleFeeComponent;
    reservationFee?: ReservationFeeComponent;
  };
  specialDates?: SpecialDatePricing[];
  discounts?: DiscountConfig[]; // For future use
  metadata?: Record<string, any>;
}

// Database models for frontend
export interface FeeTemplate {
  id: string;
  name: string;
  description: string | null;
  currency: string;
  feeStructure: FeeStructure; // Parsed from JSON
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  usageCount?: number;
}

export interface FeeSchedule {
  id: string;
  name: string;
  templateId: string | null;
  entityType: FeeEntityType;
  entityId: string;
  priority: number;
  feeStructure: FeeStructure | null; // Parsed from JSON
  currency: string | null;
  effectiveFrom: string;
  effectiveUntil: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  template?: FeeTemplate;
  entity?: {
    id: string;
    name: string;
    type: FeeEntityType;
  };
}

// API request/response types
export interface CreateFeeTemplateRequest {
  name: string;
  description?: string;
  currency: string;
  feeStructure: FeeStructure;
}

export interface UpdateFeeTemplateRequest {
  name?: string;
  description?: string | null;
  feeStructure?: FeeStructure;
  isActive?: boolean;
}

export interface CreateFeeScheduleRequest {
  name: string;
  templateId?: string;
  entityType: FeeEntityType;
  entityId: string;
  priority?: number;
  feeStructure?: FeeStructure; // Required if templateId is not provided
  currency?: string; // Required if templateId is not provided
  effectiveFrom: string;
  effectiveUntil?: string;
}

export interface UpdateFeeScheduleRequest {
  name?: string;
  templateId?: string | null;
  priority?: number;
  feeStructure?: FeeStructure | null;
  currency?: string | null;
  effectiveFrom?: string;
  effectiveUntil?: string | null;
  isActive?: boolean;
}

// Fee calculation types
export interface FeeCalculationRequest {
  entityType: FeeEntityType;
  entityId: string;
  session: {
    startTime: string;
    endTime: string;
    energyConsumed: number;
    peakPower?: number;
    idleTime?: number;
    chargeComplete?: boolean;
  };
  reservation?: {
    startTime: string;
    endTime: string;
    actualArrivalTime?: string;
  };
}

export interface FeeCalculationResponse {
  sessionFee: number;
  energyFee: number;
  timeFee: number;
  idleFee: number;
  reservationFee: number;
  subtotal: number;
  discounts: Array<{
    name: string;
    amount: number;
    type: 'percentage' | 'absolute';
  }>;
  totalDiscount: number;
  total: number;
  currency: string;
  breakdown: {
    sessionFee?: { amount: number; description: string };
    energyFee?: Array<{ 
      rate: number; 
      kWh: number; 
      amount: number; 
      period: string;
    }>;
    timeFee?: { 
      minutes: number; 
      rate: number; 
      amount: number;
      freeMinutes?: number;
    };
    idleFee?: { 
      minutes: number; 
      rate: number; 
      amount: number;
      graceMinutes?: number;
    };
    reservationFee?: { 
      hours: number; 
      rate: number; 
      amount: number;
      noShowPenalty?: number;
    };
  };
}

// Query types for listing endpoints
export interface ListFeeTemplatesQuery {
  search?: string;
  isActive?: boolean;
  isSystem?: boolean;
  page?: number;
  limit?: number;
  sort?: 'name' | 'createdAt' | 'updatedAt';
  order?: 'asc' | 'desc';
}

export interface ListFeeSchedulesQuery {
  entityType?: FeeEntityType;
  entityId?: string;
  templateId?: string;
  isActive?: boolean;
  effectiveOn?: string; // Date to check effectiveness
  page?: number;
  limit?: number;
  sort?: 'name' | 'priority' | 'effectiveFrom' | 'createdAt';
  order?: 'asc' | 'desc';
}

// Entity selection types for forms
export interface EntitySelection {
  type: FeeEntityType;
  id: string;
  name?: string;
}

// Utility types for forms
export interface FeePreviewRequest {
  entityType: FeeEntityType;
  entityId: string;
  estimatedDuration: number; // in minutes
  estimatedEnergy: number; // in kWh
  startTime?: string;
}

// Common choice options for selects
export const FEE_ENTITY_TYPE_CHOICES = [
  { id: 'site', name: 'Site' },
  { id: 'load_group', name: 'Load Group' },
  { id: 'charger', name: 'Charger' },
];

export const FEE_COMPONENT_TYPE_CHOICES = [
  { id: 'session_fee', name: 'Session Fee' },
  { id: 'energy_fee', name: 'Energy Fee' },
  { id: 'time_fee', name: 'Time Fee' },
  { id: 'idle_fee', name: 'Idle Fee' },
  { id: 'reservation_fee', name: 'Reservation Fee' },
];

export const DAY_TYPE_CHOICES = [
  { id: 'weekday', name: 'Weekdays' },
  { id: 'weekend', name: 'Weekends' },
  { id: 'monday', name: 'Monday' },
  { id: 'tuesday', name: 'Tuesday' },
  { id: 'wednesday', name: 'Wednesday' },
  { id: 'thursday', name: 'Thursday' },
  { id: 'friday', name: 'Friday' },
  { id: 'saturday', name: 'Saturday' },
  { id: 'sunday', name: 'Sunday' },
];

export const CURRENCY_CHOICES = [
  { id: 'HKD', name: 'Hong Kong Dollar (HKD)' },
  { id: 'USD', name: 'US Dollar (USD)' },
  { id: 'CNY', name: 'Chinese Yuan (CNY)' },
  { id: 'EUR', name: 'Euro (EUR)' },
];